import { useState, useEffect } from "react";
import { useAuthContext } from "./useAuthContext";
import { projectFirestore, timestamp } from "../../firebase-config/config";
import { useFirestore } from "./useFirestore";
import useOrders from "./useOrders"; // Add this import

const useStaffAssignments = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [assignedOrders, setAssignedOrders] = useState({
    pickup: [],
    delivery: [],
    outletHandover: [],
    history: [],
  });
  const { user } = useAuthContext();
  const { updateDocument } = useFirestore("orders");

  const { addTimelineEvent } = useOrders();

  useEffect(() => {
    setIsLoading(true);
    if (user) {
      const unsubscribe = projectFirestore.collection("orders").onSnapshot(
        async (snapshot) => {
          const allOrders = await Promise.all(
            snapshot.docs.map(async (doc) => {
              const orderData = doc.data();
              // Fetch assigned user details
              let assignedUserDetails = null;
              if (orderData.orderAssignedTo) {
                const userDoc = await projectFirestore
                  .collection("users")
                  .doc(orderData.orderAssignedTo)
                  .get();
                if (userDoc.exists) {
                  assignedUserDetails = userDoc.data();
                }
              } else {
                const userDoc = await projectFirestore
                  .collection("users")
                  .doc(orderData.createdBy)
                  .get();
                if (userDoc.exists) {
                  assignedUserDetails = userDoc.data();
                }
              }
              return {
                id: doc.id,
                ...orderData,
                assignedUserDetails,
              };
            })
          );

          // Filter pickup orders
          const pickupOrders = allOrders.filter(
            (order) =>
              order.pickupStaff?.staffId === user.phoneNumber &&
              !order.orderStages?.itemsPickedUp?.completed
          );

          // Filter delivery orders with more precise conditions
          const deliveryOrders = allOrders.filter(
            (order) =>
              order.deliveryStaff?.staffId === user.phoneNumber &&
              order.currentOrderStage === "processing" && // Explicitly check processing stage
              order.orderStages?.itemsPickedUp?.completed && // Pickup must be complete
              !order.orderStages?.delivered?.completed // Delivery not yet completed
          );

          // Filter outlet handover orders (picked up but not yet handed over at outlet)
          const outletHandoverOrders = allOrders.filter(
            (order) =>
              order.pickupStaff?.staffId === user.phoneNumber &&
              order.orderStages?.itemsPickedUp?.completed // Pickup must be complete
          );

          // History logic for completed tasks
          const completedOrders = allOrders.reduce((acc, order) => {
            const completedTasks = [];

            // Check for completed pickup
            if (
              order.pickupStaff?.staffId === user.phoneNumber &&
              order.orderStages?.itemsPickedUp?.completed
            ) {
              completedTasks.push({
                ...order,
                completedTaskType: "pickup",
              });
            }

            // Check for completed delivery
            if (
              order.deliveryStaff?.staffId === user.phoneNumber &&
              order.orderStages?.delivered?.completed
            ) {
              completedTasks.push({
                ...order,
                completedTaskType: "delivery",
              });
            }

            return [...acc, ...completedTasks];
          }, []);

          setAssignedOrders({
            pickup: pickupOrders || [],
            delivery: deliveryOrders || [],
            outletHandover: outletHandoverOrders || [],
            history: completedOrders || [],
          });
          setIsLoading(false);
        },
        (error) => {
          console.error("Error fetching orders:", error);
          setIsLoading(false);
        }
      );

      return () => unsubscribe();
    } else {
      setIsLoading(false);
    }
  }, [user]);

  const updateOrderItemsStage = async (orderId, newStage, eventDescription, staffInfo) => {
    try {
      // Query all orderItems for this order
      const orderItemsSnapshot = await projectFirestore
        .collection("orderItems")
        .where("orderId", "==", orderId)
        .get();

      if (orderItemsSnapshot.empty) {
        console.log(`No orderItems found for order ${orderId}`);
        return;
      }

      const batch = projectFirestore.batch();
      const currentTime = timestamp.fromDate(new Date());
      const currentDateTime = new Date().toISOString();

      orderItemsSnapshot.docs.forEach((doc) => {
        const orderItemRef = doc.ref;
        const orderItemData = doc.data();
        const currentHistory = orderItemData.history || [];

        // Create history entry
        const historyEntry = {
          event: eventDescription,
          timestamp: currentDateTime,
          staffId: staffInfo.staffId,
          staffName: staffInfo.staffName || staffInfo.staffId,
          stageChanged: newStage,
        };

        // Update the orderItem document
        batch.update(orderItemRef, {
          currentStage: newStage,
          history: [...currentHistory, historyEntry],
          updatedAt: currentTime,
        });
      });

      await batch.commit();
      console.log(`Updated ${orderItemsSnapshot.docs.length} orderItems for order ${orderId}`);
    } catch (error) {
      console.error("Error updating orderItems stage:", error);
      throw error;
    }
  };

  const handOverToOutlet = async (order) => {
    try {
      // Get staff information
      let staffName = user.phoneNumber;
      try {
        const staffDoc = await projectFirestore
          .collection("users")
          .doc(user.phoneNumber)
          .get();
        if (staffDoc.exists) {
          const staffData = staffDoc.data();
          staffName = staffData.displayName || staffData.fullName || user.phoneNumber;
        }
      } catch (error) {
        console.error("Error fetching staff details:", error);
      }

      // Update orderItems collection to "arrived-outlet" stage
      await updateOrderItemsStage(
        order.id,
        "arrived-outlet",
        "Driver handed over order items at outlet",
        {
          staffId: user.phoneNumber,
          staffName: staffName,
        }
      );
    } catch (error) {
      console.error("Error handing over to outlet:", error);
      throw error;
    }
  };

  const markTaskComplete = async (order, taskType) => {
    try {
      // Get staff information
      let staffName = user.phoneNumber;
      try {
        const staffDoc = await projectFirestore
          .collection("users")
          .doc(user.phoneNumber)
          .get();
        if (staffDoc.exists) {
          const staffData = staffDoc.data();
          staffName = staffData.displayName || staffData.fullName || user.phoneNumber;
        }
      } catch (error) {
        console.error("Error fetching staff details:", error);
      }

      if (taskType === "pickup") {
        // Update orders collection
        await updateDocument(order.id, {
          currentOrderStage: "itemsPickedUp",
          "orderStages.itemsPickedUp": {
            timestamp: timestamp.fromDate(new Date()),
            completed: true,
          },
        });

        // Update orderItems collection
        await updateOrderItemsStage(
          order.id,
          "items-picked-up",
          "Staff picked up order from customer",
          {
            staffId: user.phoneNumber,
            staffName: staffName,
          }
        );

        await addTimelineEvent(order.id, "PICKUP_COMPLETED", {
          staffId: user.phoneNumber,
          timestamp: new Date().toISOString(),
          location: order.shippingAddress,
        });
      } else if (taskType === "delivery") {
        // Update orders collection
        await updateDocument(order.id, {
          currentOrderStage: "delivered",
          orderStatus: "completed",
          "orderStages.delivered": {
            timestamp: timestamp.fromDate(new Date()),
            completed: true,
          },
        });

        // Update orderItems collection
        await updateOrderItemsStage(
          order.id,
          "order-delivered",
          "Order delivered to customer by staff",
          {
            staffId: user.phoneNumber,
            staffName: staffName,
          }
        );

        await addTimelineEvent(order.id, "DELIVERY_COMPLETED", {
          staffId: user.phoneNumber,
          timestamp: new Date().toISOString(),
          location: order.shippingAddress,
          orderTotal: order.totalPrice,
        });
      }
    } catch (error) {
      console.error("Error marking task complete:", error);
    }
  };

  return { assignedOrders, isLoading, markTaskComplete, handOverToOutlet };
};

export default useStaffAssignments;
